import {famsOf, kidsOf, parentsOf} from '../api/tree'
import type {TreeIndex} from '../api/types'

/* Карточка вертикальная: крупное фото сверху, подписи под ним.
   Раньше была горизонтальная строка с аватаркой 38px — лицо в ней
   не читалось, а древо про лица. */
export const CARD_W = 168
export const CARD_H = 250
/* Просвет между поколениями держим широким осознанно.
   Замер на боевых данных: в одном промежутке бывает до восьми семей,
   и при прежних 92px их шины расходились на 9.7px — линии сливались
   в кашу, и чей это ребёнок, было не разобрать. Полторы сотни пикселей
   дают тем же восьми дорожкам по 18px, а древо становится выше всего
   на пятую часть. Зазор между соседями тоже увеличен: чем шире строй,
   тем реже шины перекрываются по горизонтали и тем меньше дорожек
   вообще нужно. */
export const COL_W = CARD_W + 34
export const ROW_H = CARD_H + 150
export const GAP = 30

/** Кого показываем: всю родню целиком или только кровную. */
export type Kin = 'all' | 'blood'

export interface LayoutNode {
  x: number
  y: number
}

export interface Link {
  kind: 'up' | 'couple'
  a: string
  b: string | null
  c?: string
  /** высота горизонтальной шины; у каждой семьи своя дорожка */
  bus?: number
  /** тон линии: соседние дорожки красим по-разному, иначе в плотном
   *  месте не разобрать, чья это связь */
  tone?: number
}

export interface Layout {
  pos: Map<string, LayoutNode>
  links: Link[]
  /** Кто пока ни с кем не связан — стоит отдельной полосой внизу. */
  loose: Set<string>
}

/* Все узлы одного размера: пунктирной заглушки «добавить родителей»
   больше нет — родню заводят правой кнопкой прямо на карточке. */
export const heightOf = (_id: string) => CARD_H
export const widthOf = (_id: string) => CARD_W

/** Супруги человека — все, из всех его семей. */
const partnersOf = (ix: TreeIndex, h: string): string[] =>
  famsOf(ix, h)
    .map(f => (f.father_handle === h ? f.mother_handle : f.father_handle))
    .filter((x): x is string => !!x)

/**
 * Кто попадает в древо.
 *
 * «Вся родня» — весь связный кусок: и кровные, и те, кто вошёл в семью
 * через брак, вместе со своими родителями и детьми. Это и есть общее
 * хранилище, ради которого всё затевалось.
 *
 * «Кровные» — предки корня и все их потомки: родители, деды, братья,
 * дяди, двоюродные. Супруги не входят, и их родня тоже.
 *
 * Ходить просто по связям «родитель↔ребёнок» нельзя: от общего ребёнка
 * обход перепрыгивает на второго родителя, а от него — во всю его родню,
 * и семья супруги оказывается «кровной». Поэтому вверх идём отдельно,
 * вниз — отдельно, и только по прямым связям.
 */
export function relatives(ix: TreeIndex, root: string, kin: Kin): Set<string> {
  if (kin === 'all') {
    const out = new Set<string>([root])
    const queue = [root]
    while (queue.length) {
      const h = queue.pop()!
      for (const n of [...parentsOf(ix, h), ...kidsOf(ix, h), ...partnersOf(ix, h)]) {
        if (!out.has(n) && ix.people.has(n)) {
          out.add(n)
          queue.push(n)
        }
      }
    }
    return out
  }

  // вверх: сам корень и все его предки
  const up = new Set<string>([root])
  const climb = [root]
  while (climb.length) {
    const h = climb.pop()!
    for (const p of parentsOf(ix, h)) {
      if (!up.has(p) && ix.people.has(p)) {
        up.add(p)
        climb.push(p)
      }
    }
  }
  // вниз: потомки каждого из предков — это и есть братья, дяди, кузены
  const out = new Set(up)
  const down = [...up]
  while (down.length) {
    const h = down.pop()!
    for (const k of kidsOf(ix, h)) {
      if (!out.has(k) && ix.people.has(k)) {
        out.add(k)
        down.push(k)
      }
    }
  }
  return out
}

/** Поколение относительно корня: родитель −1, ребёнок +1, супруг тот же. */
function generations(ix: TreeIndex, set: Set<string>, root: string): Map<string, number> {
  const gen = new Map<string, number>([[root, 0]])
  const queue = [root]
  for (let i = 0; i < queue.length; i++) {
    const h = queue[i]
    const g = gen.get(h)!
    const step = (list: string[], d: number) => {
      for (const n of list) {
        if (set.has(n) && !gen.has(n)) {
          gen.set(n, g + d)
          queue.push(n)
        }
      }
    }
    step(parentsOf(ix, h), -1)
    step(kidsOf(ix, h), 1)
    step(partnersOf(ix, h), 0)
  }
  // редкий случай: остров внутри выборки, до которого обход не дошёл
  for (const h of set) if (!gen.has(h)) gen.set(h, 0)
  return gen
}

/* ---------- послойная раскладка ----------
 *
 * Схема классическая для слоистых графов и та же по сути, по какой строит
 * «Древо жизни»: сначала выбирается ПОРЯДОК узлов в каждом поколении и
 * только потом — координаты. Прежний вариант считал координаты сразу, по
 * ходу обхода, и порядок получался какой выйдет — отсюда длинные косые
 * линии и частые пересечения, которые не лечились точечными правками.
 *
 * Три этапа:
 *   1. Узлы объединяются в «ячейки»: супруги в одну, остальные по одному.
 *      Пара не должна разъезжаться, поэтому переставляем именно ячейки.
 *   2. Порядок в строке подбирается медианным методом с обменами соседей:
 *      ячейка тянется к середине своих соседей сверху или снизу,
 *      направление чередуется, лучший по числу пересечений вариант
 *      запоминается.
 *   3. Координаты расставляются по выбранному порядку: каждая ячейка
 *      тянется к середине соседей, затем зазоры разжимаются так, чтобы
 *      никто не налез на соседа и порядок не сломался.
 */

/** Ячейка строки: супружеская пара или одиночка. */
interface Cell {
  people: string[]
  gen: number
  /** место в строке; им и оперирует перестановка */
  order: number
  x: number
}

const cellW = (c: Cell) => (c.people.length > 1 ? CARD_W * 2 + GAP : CARD_W)

/** Насколько человек смещён от середины своей ячейки. В паре — на
 *  полкарточки в свою сторону, поэтому линия к нему идёт не в центр. */
const shift = (c: Cell, who: number) =>
  c.people.length > 1 ? (who === 0 ? -(CARD_W + GAP) / 2 : (CARD_W + GAP) / 2) : 0

/** Куда именно приходит связь: точка человека, а не центр ячейки. */
interface Anchor {
  /** ячейка родителей */
  from: Cell
  /** ячейка ребёнка и его место в ней */
  to: Cell
  who: number
}

/** Сколько пересечений дают рёбра между двумя соседними строками. */
function crossings(edges: [number, number][]): number {
  let n = 0
  for (let i = 0; i < edges.length; i++) {
    for (let j = i + 1; j < edges.length; j++) {
      if ((edges[i][0] - edges[j][0]) * (edges[i][1] - edges[j][1]) < 0) n++
    }
  }
  return n
}

export function layout(
  ix: TreeIndex,
  rootId: string,
  kin: Kin,
  /** внутреннее: раскладываем отдельный островок, второй раз его искать не надо */
  island = false,
): Layout {
  const set = relatives(ix, rootId, kin)
  const gen = generations(ix, set, rootId)

  /* ---------- 1. ячейки ---------- */
  const cellOf = new Map<string, Cell>()
  const cells: Cell[] = []
  for (const h of set) {
    if (cellOf.has(h)) continue
    const g = gen.get(h) ?? 0
    // супруга берём того, с кем общая семья, кто свободен и в том же ряду
    const sp = partnersOf(ix, h).find(s => set.has(s) && !cellOf.has(s) && (gen.get(s) ?? 0) === g)
    const c: Cell = {people: sp ? [h, sp] : [h], gen: g, order: 0, x: 0}
    cells.push(c)
    cellOf.set(h, c)
    if (sp) cellOf.set(sp, c)
  }

  /* ---------- рёбра между поколениями ---------- */
  const kidsOfCell = new Map<Cell, Cell[]>()
  const parentsOfCell = new Map<Cell, Cell[]>()
  const anchors: Anchor[] = []
  const add = (m: Map<Cell, Cell[]>, k: Cell, v: Cell) => {
    const list = m.get(k)
    if (list) { if (!list.includes(v)) list.push(v) } else m.set(k, [v])
  }
  for (const f of ix.families.values()) {
    const par = [f.father_handle, f.mother_handle].filter((h): h is string => !!h && set.has(h))
    if (!par.length) continue
    const pc = cellOf.get(par[0])!
    for (const cr of f.child_ref_list || []) {
      if (!set.has(cr.ref)) continue
      const cc = cellOf.get(cr.ref)!
      if (cc === pc) continue
      add(kidsOfCell, pc, cc)
      add(parentsOfCell, cc, pc)
      anchors.push({from: pc, to: cc, who: cc.people.indexOf(cr.ref)})
    }
  }

  /* ---------- 2. порядок в строках ---------- */
  const gens = [...new Set(cells.map(c => c.gen))].sort((a, b) => a - b)
  const rows = new Map<number, Cell[]>(gens.map(g => [g, cells.filter(c => c.gen === g)]))

  /* Начальный порядок — обходом в глубину от родоначальников: так семьи
     ложатся сплошными кусками, а не вперемешку. Дальше медиана только
     подправляет, а не собирает древо с нуля. */
  const seq = new Map<Cell, number>()
  let tick = 0
  const walk = (c: Cell) => {
    if (seq.has(c)) return
    seq.set(c, tick++)
    for (const k of kidsOfCell.get(c) || []) walk(k)
  }
  for (const c of cells) if (!(parentsOfCell.get(c) || []).length) walk(c)
  for (const c of cells) walk(c)
  for (const g of gens) {
    const row = rows.get(g)!
    row.sort((a, b) => seq.get(a)! - seq.get(b)!)
    row.forEach((c, i) => { c.order = i })
  }

  /** Рёбра между строкой g и следующей, в номерах порядка. */
  const edgesBetween = (g: number): [number, number][] => {
    const out: [number, number][] = []
    for (const c of rows.get(g) || []) {
      for (const k of kidsOfCell.get(c) || []) {
        if (k.gen === g + 1) out.push([c.order, k.order])
      }
    }
    return out
  }
  const totalCross = () => gens.reduce((n, g) => n + crossings(edgesBetween(g)), 0)

  const median = (c: Cell, from: Map<Cell, Cell[]>): number => {
    const ps = (from.get(c) || []).map(v => v.order).sort((a, b) => a - b)
    if (!ps.length) return -1
    const m = ps.length >> 1
    return ps.length % 2 ? ps[m] : (ps[m - 1] + ps[m]) / 2
  }

  const renumber = (row: Cell[]) => row.forEach((c, i) => { c.order = i })
  const snapshot = () => new Map(cells.map(c => [c, c.order]))
  const restore = (s: Map<Cell, number>) => {
    for (const [c, o] of s) c.order = o
    for (const g of gens) rows.get(g)!.sort((a, b) => a.order - b.order)
  }

  let best = snapshot()
  let bestCross = totalCross()

  for (let pass = 0; pass < 16 && bestCross > 0; pass++) {
    // медиана: тянем строку к середине соседей сверху или снизу
    const downward = pass % 2 === 0
    for (const g of downward ? gens : [...gens].reverse()) {
      const row = rows.get(g)!
      const from = downward ? parentsOfCell : kidsOfCell
      const key = new Map(row.map(c => [c, median(c, from)]))
      // ячейки без соседей остаются на своих местах — иначе они гуляют
      // по строке без всякой пользы и ломают уже найденный порядок
      const fixed = row.map(c => (key.get(c)! < 0))
      const movable = row.filter(c => !fixed[row.indexOf(c)])
        .sort((a, b) => key.get(a)! - key.get(b)!)
      const next: Cell[] = []
      let mi = 0
      for (let i = 0; i < row.length; i++) next.push(fixed[i] ? row[i] : movable[mi++])
      rows.set(g, next)
      renumber(next)
    }

    // обмены соседей: медиана до конца не доводит
    for (let sweep = 0; sweep < 4; sweep++) {
      let moved = false
      for (const g of gens) {
        const row = rows.get(g)!
        for (let i = 0; i + 1 < row.length; i++) {
          const before = crossings(edgesBetween(g - 1)) + crossings(edgesBetween(g))
          ;[row[i], row[i + 1]] = [row[i + 1], row[i]]
          renumber(row)
          if (crossings(edgesBetween(g - 1)) + crossings(edgesBetween(g)) < before) moved = true
          else {
            ;[row[i], row[i + 1]] = [row[i + 1], row[i]]
            renumber(row)
          }
        }
      }
      if (!moved) break
    }

    const now = totalCross()
    if (now < bestCross) { bestCross = now; best = snapshot() }
  }
  restore(best)

  /* ---------- 3. координаты ---------- */
  const SEP = COL_W - CARD_W

  /* Между роднёй зазор обычный, между разными семьями — втрое шире.
   *
   * Раньше зазор был один на всех, и строка укладывалась как один
   * сплошной строй: чтобы дать место одной семье, приходилось двигать
   * весь ряд, и дети переставали попадать под родителей — связи шли
   * наискось через полэкрана. Развести соседние семьи дешевле, чем
   * ломать линии: древо становится шире, зато каждая семья стоит своей
   * группой и спуск к детям идёт почти отвесно. */
  const SEP_FAR = SEP * 2

  /** Родительские ячейки каждой ячейки — по ним и понятно, родня ли соседи. */
  const parentCells = new Map<Cell, Set<Cell>>()

  const sepAt = (a: Cell, b: Cell) => {
    const pa = parentCells.get(a)
    const pb = parentCells.get(b)
    if (!pa?.size || !pb?.size) return SEP_FAR
    for (const p of pb) if (pa.has(p)) return SEP
    return SEP_FAR
  }

  /** Разложить строку по желаемым точкам, не ломая порядок и зазоры. */
  const spread = (row: Cell[], want: number[]) => {
    const n = row.length
    const half = row.map(c => cellW(c) / 2)
    const gapAt = (i: number) => half[i - 1] + sepAt(row[i - 1], row[i]) + half[i]
    const x = want.slice()
    for (let i = 1; i < n; i++) x[i] = Math.max(x[i], x[i - 1] + gapAt(i))
    for (let i = n - 2; i >= 0; i--) x[i] = Math.min(x[i], x[i + 1] - gapAt(i + 1))
    for (let i = 1; i < n; i++) x[i] = Math.max(x[i], x[i - 1] + gapAt(i))
    row.forEach((c, i) => { c.x = x[i] })
  }

  // начальная плотная укладка
  for (const a of anchors) {
    if (Math.abs(a.to.gen - a.from.gen) !== 1) continue
    const set = parentCells.get(a.to) || new Set<Cell>()
    set.add(a.from)
    parentCells.set(a.to, set)
  }

  for (const g of gens) {
    const row = rows.get(g)!
    let cur = 0
    row.forEach((c, i) => {
      if (i) cur += sepAt(row[i - 1], c)
      c.x = cur + cellW(c) / 2
      cur += cellW(c)
    })
  }

  /* Тянем не центр ячейки к центру соседней, а ТОЧКУ ЧЕЛОВЕКА к точке,
     откуда выходит связь. Иначе женатый ребёнок никогда не встаёт под
     родителями: он смещён внутри своей пары на полкарточки, и линия
     всегда идёт наискось. */
  const byChild = new Map<Cell, Anchor[]>()
  const byParent = new Map<Cell, Anchor[]>()
  for (const a of anchors) {
    if (Math.abs(a.to.gen - a.from.gen) !== 1) continue
    ;(byChild.get(a.to) || byChild.set(a.to, []).get(a.to)!).push(a)
    ;(byParent.get(a.from) || byParent.set(a.from, []).get(a.from)!).push(a)
  }
  /* Медиана, а не среднее: у родителя пятерых детей среднее уводит его
     к тому краю, где детей больше, и линии к остальным ложатся наискось.
     Медиана держит его над серединой строя. */
  const avg = (xs: number[]) => {
    if (!xs.length) return null
    const a = [...xs].sort((p, q) => p - q)
    const m = a.length >> 1
    return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2
  }

  /* Тянем СРАЗУ с обеих сторон — и к родителям, и к детям.
   *
   * Поочерёдная тяга (сначала всех к родителям, потом всех к детям)
   * оставляла слабо связанные куски там, куда их положила начальная
   * укладка: ветку мужа уносило к левому краю, а между нею и остальным
   * древом зияла дыра в три тысячи пикселей. Двусторонняя тяга подтягивает
   * такой кусок к его настоящему месту с первого прохода.
   *
   * Последний проход — только сверху вниз: он ставит детей ровно под
   * родителей, и портить это уже некому. */
  const PASSES = 18
  for (let pass = 0; pass < PASSES; pass++) {
    // хвост — только сверху вниз: он ставит детей под родителей, и одного
    // прохода мало, соседи успевают растолкать уже поставленное
    const last = pass >= PASSES - 4
    for (const g of last || pass % 2 ? gens : [...gens].reverse()) {
      const row = rows.get(g)!
      const want = row.map(c => {
        const up = (byChild.get(c) || []).map(a => a.from.x - shift(c, a.who))
        const dn = (byParent.get(c) || []).map(a => a.to.x + shift(a.to, a.who))
        return avg(last ? up : [...up, ...dn]) ?? c.x
      })
      spread(row, want)
    }
  }

  /* ---------- ячейки -> люди ---------- */
  const pos = new Map<string, LayoutNode>()
  for (const c of cells) {
    const y = c.gen * ROW_H
    if (c.people.length > 1) {
      pos.set(c.people[0], {x: c.x - (CARD_W + GAP) / 2, y})
      pos.set(c.people[1], {x: c.x + (CARD_W + GAP) / 2, y})
    } else {
      pos.set(c.people[0], {x: c.x, y})
    }
  }

  /* ---------- связи ----------
     Собираем из семей, а не по ходу раскладки: так одинаково ложатся
     и обычные ветки, и браки между двумя ветками древа. */
  const links: Link[] = []
  for (const f of ix.families.values()) {
    const fa = f.father_handle && set.has(f.father_handle) ? f.father_handle : null
    const mo = f.mother_handle && set.has(f.mother_handle) ? f.mother_handle : null
    if (fa && mo) links.push({kind: 'couple', a: fa, b: mo})
    const parent = fa || mo
    if (!parent) continue
    for (const c of f.child_ref_list || []) {
      if (set.has(c.ref)) links.push({kind: 'up', a: parent, b: fa && mo ? mo : null, c: c.ref})
    }
  }

  /* ---------- ещё не связанные ----------
   *
   * Человек без родителей, супруга и детей не попадает в древо вовсе:
   * дойти до него по связям неоткуда. Заводят таких постоянно — записал
   * бабушкину сестру, связи проставишь потом, — и до этого «потом» они
   * пропадали из виду совсем.
   *
   * Кладём их отдельной полосой под древом: они видны, по правой кнопке
   * им можно завести родню, но с самим древом не путаются — ни линий,
   * ни места в поколениях. */
  const loose = new Set<string>()
  if (kin === 'all' && !island) {
    /* Островки раскладываем ТЕМ ЖЕ алгоритмом и ставим под древом.
     *
     * Раньше сюда попадали только совсем одинокие — у кого нет ни
     * родителей, ни супруга, ни детей. Стоило завести такому человеку
     * ребёнка, как они вдвоём образовывали свой островок и пропадали
     * отовсюду: в древо не входят, в полосу одиноких больше не годятся.
     * Теперь берём всех, до кого от корня не дойти, и каждый островок
     * получает нормальные поколения и связи — просто отдельно. */
    const rest = new Set([...ix.people.keys()].filter(h => !set.has(h)))
    let y = (Math.max(...[...pos.values()].map(p => p.y)) || 0) + Math.round(ROW_H * 1.15)
    let x = 0
    while (rest.size) {
      const seed = [...rest][0]
      const sub = layout(ix, seed, 'all', true)
      const xs = [...sub.pos.values()].map(p => p.x)
      const dx = x - Math.min(...xs)
      const dy = y - Math.min(...[...sub.pos.values()].map(p => p.y))
      sub.pos.forEach((p, h) => {
        pos.set(h, {x: p.x + dx, y: p.y + dy})
        loose.add(h)
        rest.delete(h)
      })
      for (const l of sub.links) links.push({...l, bus: l.bus === undefined ? undefined : l.bus + dy})
      x += Math.max(...xs) - Math.min(...xs) + CARD_W + COL_W
    }
  }

  // корень — в начало координат: вошедший смотрит на древо от себя
  const rx = pos.get(rootId)?.x ?? 0
  pos.forEach(v => (v.x -= rx))

  const valid = links.filter(l => pos.has(l.a) && (!l.b || pos.has(l.b)) && (!l.c || pos.has(l.c)))
  assignBuses(valid, pos)
  return {pos, links: valid, loose}
}


/** Расстояние между шинами соседних семей. Подобрано под просвет:
 *  при восьми дорожках (максимум на боевых данных) они как раз
 *  укладываются, не прижимаясь друг к другу. */
const LANE = 18

/**
 * Разводим горизонтальные шины по дорожкам.
 *
 * Раньше шина каждой семьи лежала ровно посередине между строками, и в
 * широком древе десяток семей давал одну сплошную линию через весь экран:
 * непонятно, чей это ребёнок. Теперь семьи, чьи шины пересекаются по
 * горизонтали, разводятся по разным высотам — как в «Древе жизни».
 *
 * Раскладка жадная: сортируем по левому краю и кладём в первую дорожку,
 * где уже уложенная шина кончилась левее. Это не оптимально по числу
 * дорожек, но гарантирует, что две шины на одной высоте не пересекутся.
 */
/** Ключ пары: по нему связь к детям и перемычка между супругами
 *  опознаются как одна семья. */
const coupleKey = (l: Link) => (l.b ? `${l.a}|${l.b}` : l.a)

function assignBuses(links: Link[], pos: Map<string, LayoutNode>) {
  /** тон каждой пары — чтобы покрасить в него же и перемычку супругов */
  const toneByCouple = new Map<string, number>()
  const byRow = new Map<number, {l: Link; x0: number; x1: number}[]>()
  for (const l of links) {
    if (l.kind !== 'up' || !l.c) continue
    const a = pos.get(l.a)!
    const b = l.b ? pos.get(l.b) : null
    const c = pos.get(l.c)!
    const px = b ? (a.x + b.x) / 2 : a.x
    const row = byRow.get(c.y) || []
    row.push({l, x0: Math.min(px, c.x), x1: Math.max(px, c.x)})
    byRow.set(c.y, row)
  }

  for (const [childY, items] of byRow) {
    items.sort((p, q) => p.x0 - q.x0)
    const laneEnd: number[] = []
    const lanes: number[] = []
    for (const it of items) {
      let lane = laneEnd.findIndex(end => end < it.x0 - LANE)
      if (lane < 0) { lane = laneEnd.length; laneEnd.push(0) }
      laneEnd[lane] = it.x1
      lanes.push(lane)
    }

    // шаг ужимаем под фактическое число дорожек: если их больше, чем
    // влезает по-хорошему, лучше потесниться, чем положить две шины
    // на одну высоту — ровно от этого линии и сливались
    const top = childY - CARD_H / 2
    const parentBottom = childY - ROW_H + CARD_H / 2
    const room = top - parentBottom
    const step = laneEnd.length > 1
      ? Math.min(LANE, (room - 24) / (laneEnd.length - 1))
      : LANE
    /* Цвет — по ПАРЕ РОДИТЕЛЕЙ, а не по дорожке. Дорожек у одной семьи
       бывает несколько, и раскраска по ним разводила детей одной пары в
       разные цвета — ровно наоборот тому, зачем цвет нужен. Порядок
       групп — слева направо, поэтому соседние семьи всегда разного тона. */
    const toneOf = new Map<string, number>()
    items.forEach((it, i) => {
      it.l.bus = top - (14 + lanes[i] * step)
      const key = coupleKey(it.l)
      if (!toneOf.has(key)) toneOf.set(key, toneOf.size)
      it.l.tone = toneOf.get(key)! % 2
      toneByCouple.set(key, it.l.tone)
    })
  }

  /* Перемычка между супругами — того же тона, что и спуски к их детям:
     семья читается целиком, от пары до последнего ребёнка. У бездетной
     пары тона нет и взяться ему неоткуда — она остаётся обычной. */
  for (const l of links) {
    if (l.kind !== 'couple') continue
    const tone = toneByCouple.get(coupleKey(l)) ?? toneByCouple.get(`${l.b}|${l.a}`)
    if (tone !== undefined) l.tone = tone
  }
}

/** Путь связи. Родителей соединяет горизонтальная шина, от её центра
 *  идёт спуск к ребёнку. */
export function pathFor(l: Link, cur: Record<string, {x: number; y: number}>): string | null {
  if (l.kind === 'couple') {
    const a = cur[l.a]
    const b = l.b ? cur[l.b] : null
    if (!a || !b) return null
    return `M${Math.min(a.x, b.x) + CARD_W / 2},${a.y}H${Math.max(a.x, b.x) - CARD_W / 2}`
  }
  const a = cur[l.a]
  const b = l.b ? cur[l.b] : null
  const c = l.c ? cur[l.c] : null
  if (!a || !c || !l.c) return null

  const bus = l.bus ?? (a.y + c.y) / 2
  const px = b ? (a.x + b.x) / 2 : a.x
  // От пары спуск начинается прямо с линии брака — она проходит по центру
  // карточек, ровно через промежуток между ними. Раньше вертикаль стартовала
  // от нижней кромки и висела в воздухе, не касаясь ничего.
  const from = b ? a.y : a.y + heightOf(l.a) / 2
  let d = `M${px},${from}V${bus}`
  d += `M${px},${bus}H${c.x}V${c.y - heightOf(l.c) / 2}`
  return d
}

export function bounds(pos: Map<string, LayoutNode>) {
  let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity
  pos.forEach((v, id) => {
    const w = widthOf(id) / 2
    const h = heightOf(id) / 2
    x0 = Math.min(x0, v.x - w); x1 = Math.max(x1, v.x + w)
    y0 = Math.min(y0, v.y - h); y1 = Math.max(y1, v.y + h)
  })
  return {x0, x1, y0, y1}
}
